import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  LayoutDashboard, 
  Users, 
  Calculator, 
  Calendar,
  Target,
  FileText,
  Eye,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  UserPlus,
  Wallet,
  Crown,
  CalendarDays,
  ClipboardList,
  UserSearch,
  Mail,
  Instagram,
  DollarSign,
  MessageCircle,
  Sun,
  Moon
} from 'lucide-react';

const Sidebar = ({ activeTab, setActiveTab, isCollapsed, setIsCollapsed, onLogout, onCreateUser }) => {
  const { isDark, toggleTheme } = useTheme();
  // Items can be either flat (with `id`) or a group (with `children` and a `key`)
  const menuItems = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'reportes', label: 'Reportes', icon: ClipboardList },
    { id: 'ventas-mym', label: 'Ventas MYM', icon: DollarSign },
    {
      key: 'crm-group',
      label: 'CRM',
      icon: UserSearch,
      children: [
        { id: 'crm', label: 'Spenders', icon: UserSearch },
        { id: 'metas', label: 'Metas', icon: Target },
        { id: 'solicitudes', label: 'Solicitudes', icon: ClipboardList },
        { id: 'jgmpro', label: 'JGM PRO', icon: Instagram },
        { id: 'chatterly', label: 'Chatterly', icon: MessageCircle },
        { id: 'email-aliases', label: 'Buzón IG', icon: Mail },
        { id: 'calculadora', label: 'Calculadora', icon: Calculator },
      ],
    },
    { id: 'settings', label: 'Empleados', icon: Users },
    { id: 'models', label: 'Modelos', icon: Crown },
    { id: 'contabilidad', label: 'Contabilidad', icon: Calculator },
    { id: 'asistencia', label: 'Asistencia', icon: Calendar },
    { id: 'monitoreo', label: 'Monitoreo', icon: Eye },
    { id: 'nomina', label: 'Nómina', icon: Wallet },
    { id: 'agenda', label: 'Agenda', icon: CalendarDays },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
  ];

  // Auto-expand the CRM group when one of its children is active
  const [expanded, setExpanded] = useState(() => {
    const init = new Set();
    menuItems.forEach((it) => {
      if (it.children && it.children.some((c) => c.id === activeTab)) {
        init.add(it.key);
      }
    });
    return init;
  });

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <>
      {/* Overlay cuando el sidebar está abierto */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsCollapsed(true)}
        />
      )}
      
      <div 
        className={`fixed left-0 top-0 h-full bg-black/95 backdrop-blur-xl border-r border-white/10 transition-all duration-300 z-50 flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.5)] ${
          isCollapsed ? '-translate-x-full opacity-0' : 'translate-x-0 opacity-100'
        } w-64`}
      >
        {/* Close button */}
        <button
          onClick={() => setIsCollapsed(true)}
          className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/50">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        {/* Logo */}
        <div className="h-20 flex items-center px-4 border-b border-white/5">
          <img 
            src="https://customer-assets.emergentagent.com/job_46a23635-e3a8-4c01-958f-4141d61cb2ea/artifacts/81fohwa6_Disen%CC%83o%20sin%20ti%CC%81tulo.png" 
            alt="JG Models Agency" 
            className="h-14 w-auto object-contain"
          />
        </div>

        {/* Menu Items */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-3 space-y-1">
            {menuItems.map((item) => {
              // Render group with dropdown children
              if (item.children) {
                const isOpen = expanded.has(item.key);
                const hasActiveChild = item.children.some((c) => c.id === activeTab);
                const Icon = item.icon;
                return (
                  <div key={item.key}>
                    <button
                      onClick={() => toggleGroup(item.key)}
                      data-testid={`sidebar-group-${item.key}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                        hasActiveChild
                          ? 'bg-white/10 text-white'
                          : 'text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <button
                              key={child.id}
                              onClick={() => {
                                setActiveTab(child.id);
                                setIsCollapsed(true);
                              }}
                              data-testid={`sidebar-item-${child.id}`}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                                activeTab === child.id
                                  ? 'bg-white/10 text-white'
                                  : 'text-white/60 hover:text-white hover:bg-white/5'
                              }`}
                            >
                              <ChildIcon size={16} />
                              <span className="text-sm font-medium">{child.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Render flat item
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsCollapsed(true);
                  }}
                  data-testid={`sidebar-item-${item.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === item.id
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon size={20} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="px-3 mt-6 space-y-1">
            <p className="text-xs text-white/30 uppercase tracking-wider px-3 mb-2">Acciones</p>
            
            <button
              onClick={() => {
                onCreateUser();
                setIsCollapsed(true);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-all"
            >
              <UserPlus size={20} />
              <span className="text-sm font-medium">Crear Usuario</span>
            </button>
          </div>
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-white/5 space-y-1">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all"
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
            <span className="text-sm font-medium">{isDark ? 'Modo Claro' : 'Modo Oscuro'}</span>
          </button>
          <button
            onClick={() => {
              onLogout();
              setIsCollapsed(true);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-all"
          >
            <LogOut size={20} />
            <span className="text-sm font-medium">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
